import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, ExternalLink, MapPin, User } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import CityAutocomplete from "@/components/CityAutocomplete";
import ClientAutocomplete from "@/components/orders/ClientAutocomplete";
import ProfitabilityGauge from "@/components/ProfitabilityGauge";
import OrderProgressSection from "@/components/orders/OrderProgressSection";
import { api, formatCurrency } from "@/lib/api";

/**
 * OrderFormDialog — formulaire de création / édition d'une commande.
 * Extrait de `OrdersPage.jsx` pour alléger ce dernier.
 *
 * Le parent reste maître du state (formData, handlers, options…), on évite
 * ainsi de dupliquer la logique métier (validation, calcul TTC, prefill type…).
 */
const OrderFormDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  editingOrder,
  profitability,
  orderTypes,
  sellers,
  subcontractors,
  onSubmit,
  onProgressChange,
}) => {
  const navigate = useNavigate();

  // Phase 179 — fetch client profile (addresses + interlocutors) when the client changes
  const [clientProfile, setClientProfile] = useState(null);
  const clientName = formData.client_name;
  const clientEmail = formData.client_email;
  useEffect(() => {
    if (!open) return;
    const name = (clientName || "").trim();
    const email = (clientEmail || "").trim().toLowerCase();
    if (!name && !email) { setClientProfile(null); return; }
    const key = email ? `email:${email}` : `name:${name.toLowerCase()}`;
    let cancelled = false;
    const t = setTimeout(() => {
      api.getClientDetail(key, { include_imported: false })
        .then((r) => { if (!cancelled) setClientProfile(r.data?.header || null); })
        .catch(() => { if (!cancelled) setClientProfile(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [clientName, clientEmail, open]);

  const profileAddresses = (clientProfile?.addresses || []).filter(a => a.line1 || a.city || a.postal_code);
  const profileInterlocutors = (clientProfile?.interlocutors || []).filter(it => it.name || it.phone || it.email);

  const importAddressIntoSite = (addr) => {
    setFormData((prev) => ({
      ...prev,
      site_address_line1: addr.line1 || "",
      site_address_line2: addr.line2 || "",
      site_postal_code: addr.postal_code || "",
      site_city: addr.city || "",
      site_country: addr.country || "",
    }));
  };

  const handleInterlocutorPick = (idx) => {
    if (idx === "-1" || idx === -1) {
      // Use main client contact
      setFormData((prev) => ({
        ...prev,
        client_interlocutor_index: -1,
        client_interlocutor_name: clientProfile?.client_name || prev.client_name || "",
        client_interlocutor_role: "Contact principal",
        client_interlocutor_phone: clientProfile?.phone || "",
        client_interlocutor_email: clientProfile?.client_email || clientEmail || "",
      }));
      return;
    }
    if (idx === "__none__") {
      setFormData((prev) => ({
        ...prev,
        client_interlocutor_index: null,
        client_interlocutor_name: null,
        client_interlocutor_role: null,
        client_interlocutor_phone: null,
        client_interlocutor_email: null,
      }));
      return;
    }
    const i = parseInt(idx, 10);
    const it = profileInterlocutors[i];
    if (!it) return;
    setFormData((prev) => ({
      ...prev,
      client_interlocutor_index: i,
      client_interlocutor_name: it.name || "",
      client_interlocutor_role: it.role || "",
      client_interlocutor_phone: it.phone || "",
      client_interlocutor_email: it.email || "",
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {editingOrder ? "Modifier la commande" : "Nouvelle commande"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-6">
          {editingOrder && profitability?.[editingOrder.id] && (
            <ProfitabilityGauge data={profitability[editingOrder.id]} />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="order-reference">
                Numéro de commande {!editingOrder && <span className="text-xs text-muted-foreground">(laisser vide pour génération automatique)</span>}
              </Label>
              <Input
                id="order-reference"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                placeholder={editingOrder ? "" : "Ex: CUSTOM-2026-0001"}
                data-testid="order-reference-input"
                className="font-mono"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="order_date">Date de commande</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="order-date-input"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.order_date
                      ? format(formData.order_date, "dd MMMM yyyy", { locale: fr })
                      : <span className="text-muted-foreground">Sélectionner une date (optionnel)</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.order_date}
                    onSelect={(date) => setFormData({ ...formData, order_date: date || null })}
                    locale={fr}
                    initialFocus
                  />
                  {formData.order_date && (
                    <div className="p-2 border-t flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({ ...formData, order_date: null })}
                        data-testid="order-date-clear"
                      >
                        Effacer
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="order-work-description">Description des travaux</Label>
              <Textarea
                id="order-work-description"
                rows={2}
                value={formData.work_description}
                onChange={(e) => setFormData({ ...formData, work_description: e.target.value })}
                placeholder="Ex : peinture de la face sud, travaux sur le bâtiment X…"
                data-testid="order-work-description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_name">Client *</Label>
              <ClientAutocomplete
                value={formData.client_name}
                onChange={(v) => setFormData({ ...formData, client_name: v })}
                onPickClient={(c) => setFormData((prev) => ({
                  ...prev,
                  client_name: c.client_name || prev.client_name,
                  client_email: c.client_email || prev.client_email,
                  city: c.city || prev.city,
                }))}
                required
                testId="order-client-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_email">Email du client</Label>
              <Input
                id="client_email"
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                placeholder="client@exemple.fr"
                data-testid="order-client-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Ville</Label>
              <CityAutocomplete
                value={formData.city}
                onChange={(v) => setFormData({ ...formData, city: v })}
                data-testid="order-city-input"
              />
            </div>

            {/* Phase 179 — Adresse complète du chantier */}
            <div className="md:col-span-2 border rounded-md p-3 bg-slate-50 space-y-2" data-testid="order-site-address-section">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-primary" /> Adresse du chantier
                </Label>
                {profileAddresses.length > 0 && (
                  <Select onValueChange={(v) => {
                    if (v === "__none__") return;
                    const addr = profileAddresses[parseInt(v, 10)];
                    if (addr) importAddressIntoSite(addr);
                  }}>
                    <SelectTrigger className="h-7 text-xs w-auto" data-testid="order-import-client-address">
                      <SelectValue placeholder="Importer depuis adresses client…" />
                    </SelectTrigger>
                    <SelectContent>
                      {profileAddresses.map((a, i) => (
                        <SelectItem key={a.id || i} value={String(i)} data-testid={`import-address-${i}`}>
                          {a.label || "Adresse"} — {[a.line1, a.postal_code, a.city].filter(Boolean).join(", ")}
                          {a.is_default ? " (principale)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="md:col-span-12">
                  <Label className="text-xs">Adresse (ligne 1)</Label>
                  <Input
                    value={formData.site_address_line1 || ""}
                    onChange={(e) => setFormData({ ...formData, site_address_line1: e.target.value })}
                    placeholder="42 rue du chantier"
                    data-testid="order-site-line1"
                  />
                </div>
                <div className="md:col-span-12">
                  <Label className="text-xs">Complément (ligne 2)</Label>
                  <Input
                    value={formData.site_address_line2 || ""}
                    onChange={(e) => setFormData({ ...formData, site_address_line2: e.target.value })}
                    placeholder="Bâtiment B / Appt 3"
                    data-testid="order-site-line2"
                  />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-xs">CP</Label>
                  <Input
                    value={formData.site_postal_code || ""}
                    onChange={(e) => setFormData({ ...formData, site_postal_code: e.target.value })}
                    placeholder="84430"
                    data-testid="order-site-postal"
                  />
                </div>
                <div className="md:col-span-6">
                  <Label className="text-xs">Ville</Label>
                  <Input
                    value={formData.site_city || ""}
                    onChange={(e) => setFormData({ ...formData, site_city: e.target.value })}
                    placeholder="Mondragon"
                    data-testid="order-site-city"
                  />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-xs">Pays</Label>
                  <Input
                    value={formData.site_country || ""}
                    onChange={(e) => setFormData({ ...formData, site_country: e.target.value })}
                    placeholder="France"
                    data-testid="order-site-country"
                  />
                </div>
              </div>
            </div>

            {/* Phase 179 — Interlocuteur du chantier */}
            <div className="md:col-span-2 border rounded-md p-3 bg-blue-50/30 space-y-2" data-testid="order-interlocutor-section">
              <Label className="text-sm font-semibold flex items-center gap-1.5">
                <User className="h-4 w-4 text-primary" /> Interlocuteur sur le chantier
              </Label>
              {!clientName ? (
                <p className="text-xs text-muted-foreground italic">Renseigne d&apos;abord le client pour pouvoir choisir un interlocuteur.</p>
              ) : profileInterlocutors.length === 0 ? (
                <div className="text-xs text-muted-foreground">
                  <p>Aucun interlocuteur défini sur la fiche client. Les coordonnées principales du client seront utilisées sur la fiche de chantier.</p>
                  {(clientProfile?.phone || clientProfile?.client_email) && (
                    <div className="mt-1.5 flex gap-2 flex-wrap text-foreground">
                      {clientProfile.phone && <Badge variant="secondary">Tél : {clientProfile.phone}</Badge>}
                      {clientProfile.client_email && <Badge variant="secondary">Email : {clientProfile.client_email}</Badge>}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Select
                    value={
                      formData.client_interlocutor_index === null || formData.client_interlocutor_index === undefined
                        ? "__none__"
                        : String(formData.client_interlocutor_index)
                    }
                    onValueChange={handleInterlocutorPick}
                  >
                    <SelectTrigger data-testid="order-interlocutor-select">
                      <SelectValue placeholder="Choisir un interlocuteur…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" data-testid="interlocutor-none">— Aucun choix —</SelectItem>
                      <SelectItem value="-1" data-testid="interlocutor-main">Coordonnées principales du client</SelectItem>
                      {profileInterlocutors.map((it, i) => (
                        <SelectItem key={i} value={String(i)} data-testid={`interlocutor-${i}`}>
                          {it.name}{it.role ? ` (${it.role})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.client_interlocutor_name && (
                    <div className="text-xs flex gap-2 flex-wrap pt-1" data-testid="order-interlocutor-coords">
                      <span className="font-medium">{formData.client_interlocutor_name}</span>
                      {formData.client_interlocutor_role && <span className="text-muted-foreground">({formData.client_interlocutor_role})</span>}
                      {formData.client_interlocutor_phone && <Badge variant="secondary">{formData.client_interlocutor_phone}</Badge>}
                      {formData.client_interlocutor_email && <Badge variant="secondary">{formData.client_interlocutor_email}</Badge>}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount_ht">Montant HT (€) *</Label>
              <Input
                id="amount_ht"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount_ht}
                onChange={(e) => setFormData({ ...formData, amount_ht: e.target.value })}
                required
                data-testid="order-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vat_rate">Taux de TVA *</Label>
              <Select
                value={String(formData.vat_rate ?? 20)}
                onValueChange={(v) => setFormData({ ...formData, vat_rate: parseFloat(v) })}
              >
                <SelectTrigger data-testid="order-vat-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5.5">5,5%</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="20">20%</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground" data-testid="order-amount-ttc-preview">
                Montant TTC :{" "}
                <strong>
                  {formatCurrency(
                    (parseFloat(formData.amount_ht) || 0) *
                      (1 + (parseFloat(formData.vat_rate) || 0) / 100)
                  )}
                </strong>
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="order_type_id">Type de commande *</Label>
              <Select
                value={formData.order_type_id}
                onValueChange={(v) => {
                  const selected = orderTypes.find((t) => t.id === v);
                  setFormData((prev) => ({
                    ...prev,
                    order_type_id: v,
                    vat_rate: !editingOrder && selected && selected.default_vat_rate != null
                      ? selected.default_vat_rate
                      : prev.vat_rate,
                    target_margin_percent:
                      (!editingOrder || prev.target_margin_percent === "" || prev.target_margin_percent === null)
                      && selected && selected.target_margin_percent != null
                        ? selected.target_margin_percent
                        : prev.target_margin_percent,
                  }));
                }}
                required
              >
                <SelectTrigger data-testid="order-type-select">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent>
                  {orderTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({formatCurrency(t.daily_rate)}/j)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="target_margin_percent">Marge cible (%)</Label>
              <Input
                id="target_margin_percent"
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder={(() => {
                  const sel = orderTypes.find(t => t.id === formData.order_type_id);
                  return sel?.target_margin_percent != null
                    ? `Type : ${sel.target_margin_percent}%`
                    : "Ex : 40 ou 60";
                })()}
                value={formData.target_margin_percent ?? ""}
                onChange={(e) => setFormData({ ...formData, target_margin_percent: e.target.value })}
                data-testid="order-target-margin"
              />
              <p className="text-xs text-muted-foreground">
                Pré-rempli depuis le type. Laissez vide pour garder la cible du type.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="material_cost_override">Coût matière (€) — manuel</Label>
              <Input
                id="material_cost_override"
                type="number"
                min="0"
                step="0.01"
                placeholder="Laisser vide = calculé depuis les matériaux saisis"
                value={formData.material_cost_override ?? ""}
                onChange={(e) => setFormData({ ...formData, material_cost_override: e.target.value })}
                data-testid="order-material-cost-override"
              />
              <p className="text-xs text-muted-foreground">
                Si renseigné, remplace le total calculé via les matériaux saisis dans le détail de la commande (pour le calcul de marge).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="seller_id">Vendeur</Label>
              <Select
                value={formData.seller_id || "none"}
                onValueChange={(v) => setFormData({ ...formData, seller_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="order-seller-select">
                  <SelectValue placeholder="Aucun vendeur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun vendeur</SelectItem>
                  {sellers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="executants">Nombre d'exécutants</Label>
              <Input
                id="executants"
                type="number"
                min="1"
                value={formData.executants}
                onChange={(e) => setFormData({ ...formData, executants: e.target.value })}
                data-testid="order-executants-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="custom_duration">
                Durée estimée (jours)
                <span className="text-muted-foreground text-xs ml-2">
                  (laisser vide pour calcul auto)
                </span>
              </Label>
              <Input
                id="custom_duration"
                type="number"
                min="0.5"
                step="0.5"
                placeholder="Auto"
                value={formData.custom_duration}
                onChange={(e) => setFormData({ ...formData, custom_duration: e.target.value })}
                data-testid="order-duration-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Statut</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger data-testid="order-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="planning">Au planning</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="completed">Terminée</SelectItem>
                  <SelectItem value="cashed">Encaissée</SelectItem>
                  <SelectItem value="cancelled">Annulée</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Payment section */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-4">Paiements</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="deposit_paid"
                    checked={formData.deposit_paid}
                    onCheckedChange={(c) => setFormData({ ...formData, deposit_paid: c })}
                    data-testid="order-deposit-checkbox"
                  />
                  <Label htmlFor="deposit_paid">Acompte payé</Label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deposit_amount">Montant acompte (€)</Label>
                  <Input
                    id="deposit_amount"
                    type="number"
                    step="0.01"
                    placeholder={formData.amount_ht ? `${((parseFloat(formData.amount_ht) || 0) * (1 + (parseFloat(formData.vat_rate) || 0) / 100) * 0.4).toFixed(2)} (40% TTC)` : "40% TTC par défaut"}
                    value={formData.deposit_amount}
                    onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })}
                    data-testid="deposit-amount-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Par défaut: 40% du montant TTC
                  </p>
                </div>
                {formData.deposit_paid && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.deposit_date
                          ? format(formData.deposit_date, "PPP", { locale: fr })
                          : "Date de paiement"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.deposit_date}
                        onSelect={(d) => setFormData({ ...formData, deposit_date: d })}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="order_paid"
                    checked={formData.order_paid}
                    onCheckedChange={(c) => setFormData({ ...formData, order_paid: c })}
                    data-testid="order-paid-checkbox"
                  />
                  <Label htmlFor="order_paid">Commande payée (solde)</Label>
                </div>
                {formData.order_paid && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.paid_date
                          ? format(formData.paid_date, "PPP", { locale: fr })
                          : "Date de paiement"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.paid_date}
                        onSelect={(d) => setFormData({ ...formData, paid_date: d })}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          </div>

          {/* Sous-traitant */}
          <div className="space-y-3 p-4 border rounded-sm bg-muted/20">
            <Label className="text-base font-medium">Sous-traitance (optionnel)</Label>
            <p className="text-sm text-muted-foreground">
              Si un sous-traitant est assigné, cette commande ne consomme pas l'effectif ouvriers.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sous-traitant</Label>
                <Select
                  value={formData.subcontractor_id || "none"}
                  onValueChange={(v) => setFormData({ ...formData, subcontractor_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="order-subcontractor-select">
                    <SelectValue placeholder="Aucun" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun (réalisé en interne)</SelectItem>
                    {subcontractors.map((s) => {
                      const matches =
                        !formData.order_type_id ||
                        !s.order_type_ids ||
                        s.order_type_ids.length === 0 ||
                        s.order_type_ids.includes(formData.order_type_id);
                      const name = s.company_name || `${s.first_name || ""} ${s.last_name || ""}`.trim();
                      return (
                        <SelectItem key={s.id} value={s.id}>
                          {name}
                          {!matches && (
                            <span className="text-xs text-amber-600 ml-2">(autre type)</span>
                          )}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {formData.subcontractor_id && (
                <div className="space-y-2">
                  <Label>Montant contrat sous-traitance (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.subcontractor_contract_amount}
                    onChange={(e) => setFormData({ ...formData, subcontractor_contract_amount: e.target.value })}
                    data-testid="sub-contract-amount"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Options de travail le week-end */}
          <div className="space-y-3 p-4 border rounded-sm bg-muted/30">
            <Label className="text-base font-medium">Jours de travail autorisés</Label>
            <p className="text-sm text-muted-foreground">
              Par défaut, seuls les jours du lundi au vendredi sont comptés
            </p>
            <div className="flex gap-6 mt-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allow_saturday"
                  checked={formData.allow_saturday}
                  onCheckedChange={(c) => setFormData({ ...formData, allow_saturday: c })}
                  data-testid="allow-saturday-checkbox"
                />
                <Label htmlFor="allow_saturday">Autoriser le samedi</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allow_sunday"
                  checked={formData.allow_sunday}
                  onCheckedChange={(c) => setFormData({ ...formData, allow_sunday: c })}
                  data-testid="allow-sunday-checkbox"
                />
                <Label htmlFor="allow_sunday">Autoriser le dimanche</Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Notes optionnelles..."
              data-testid="order-notes-input"
            />
          </div>

          {editingOrder && (
            <OrderProgressSection order={editingOrder} onChange={onProgressChange} />
          )}

          <DialogFooter>
            {editingOrder && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/commandes/${editingOrder.id}`);
                }}
                data-testid="open-order-full-page-btn"
                title="Ouvrir la fiche complète (Logistique, Descriptif, Médias, PDF)"
                className="mr-auto"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ouvrir la fiche complète
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" data-testid="order-submit-btn">
              {editingOrder ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrderFormDialog;
